import Image from 'next/image';
import Link from 'next/link';
import HomepageHeader from '@/components/HomepageHeader';
import HomepageFooter from '@/components/HomepageFooter';

export default function Contact() {
  return (
    <div className="min-h-screen bg-[#F6EFED] page-transition">
      {/* Header Component */}
      <HomepageHeader />

      {/* Hero Section with Background */}
      <section className="relative h-[600px] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src="/about-banner.jpg"
            alt="Contact Female Fitness Finder"
            fill
            className="object-cover"
            priority
          />
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-[#4C3E581A]" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-[1920px] mx-auto h-full flex items-center justify-center absolute bottom-0 left-0 right-0 pt-[180px]">
          <div className="max-w-[842px] text-center">
            <h1 className="font-['Gotham_Rounded',sans-serif] text-[64px] font-bold leading-[76px] tracking-[-4%] text-white mb-10" style={{ marginBottom: '10px' }}>
              Contact Us
            </h1>
            <div className="flex items-center justify-center gap-2 font-['Archivo',sans-serif] text-[20px] text-white">
              <Link href="/" className="hover:opacity-80 transition-opacity">Home</Link>
              <span>/</span>
              <span className="text-[#8A7296]">Contact Us</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="bg-[#f6efed] py-[100px]">
        <div className="max-w-[1400px] mx-auto px-[20px]">
        <div className="grid lg:grid-cols-2 gap-[100px] items-center">
          {/* Left Side - Image with Contact Info */}
          <div className="relative">
            <div className="relative h-[850px] rounded-[30px] overflow-hidden">
              <Image
                src="/contact-woman.jpg"
                alt="Woman with kettlebells"
                fill
                className="object-cover"
              />
            </div>
            {/* Contact Info Overlay */}
            <div className="absolute bottom-8 left-8 right-8">
              <div className="bg-black/40 backdrop-blur-md rounded-[16px] p-[30px] flex gap-8">
                {/* Phone Number */}
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-12 h-12 bg-[#8A7296] rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-white/80 mb-1">Phone Number</p>
                    <p className="text-sm text-white font-medium">+44 123 456 7890</p>
                  </div>
                </div>

                {/* Email Address */}
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-12 h-12 bg-[#8A7296] rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-white/80 mb-1">Email Address</p>
                    <p className="text-sm text-white font-medium">info@femalefitnessfinder.co.uk</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div>
            <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#8A7296] leading-[36px] tracking-[3%] mb-2">Contact Us</p>
            <h2 className="font-['Gotham_Rounded',sans-serif] text-[50px] font-bold text-[#2E2A3A] leading-[60px] tracking-[-5%] mb-4">Get in Touch</h2>
            <p className="font-['Archivo',sans-serif] text-[18px] font-normal text-[#6e6672] leading-[36px] tracking-[3%] mb-8">
              It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout.
            </p>

            <form className="space-y-5">
              {/* First Row - Name Fields */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block font-['Archivo',sans-serif] text-[16px] font-normal text-[#6e6672] leading-[34px] tracking-[2%] mb-2">
                    First Name <span className="text-[#8A7296]">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    className="w-full p-[20px] bg-white border border-[#e8e1e6] rounded-xl text-[#2b262e] text-sm placeholder:text-[#a0969e] outline-none focus:ring-2 focus:ring-[#8A7296] focus:border-transparent transition-all"
                    placeholder="e.g. John"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block font-['Archivo',sans-serif] text-[16px] font-normal text-[#6e6672] leading-[34px] tracking-[2%] mb-2">
                    Last Name <span className="text-[#8A7296]">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    className="w-full p-[20px] bg-white border border-[#e8e1e6] rounded-xl text-[#2b262e] text-sm placeholder:text-[#a0969e] outline-none focus:ring-2 focus:ring-[#8A7296] focus:border-transparent transition-all"
                    placeholder="e.g. Doe"
                    required
                  />
                </div>
              </div>

              {/* Second Row - Email and Phone */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block font-['Archivo',sans-serif] text-[16px] font-normal text-[#6e6672] leading-[34px] tracking-[2%] mb-2">
                    Email Address <span className="text-[#8A7296]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="w-full p-[20px] bg-white border border-[#e8e1e6] rounded-xl text-[#2b262e] text-sm placeholder:text-[#a0969e] outline-none focus:ring-2 focus:ring-[#8A7296] focus:border-transparent transition-all"
                    placeholder="e.g. johndoe@example.com"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block font-['Archivo',sans-serif] text-[16px] font-normal text-[#6e6672] leading-[34px] tracking-[2%] mb-2">
                    Phone Number <span className="text-[#8A7296]">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="w-full p-[20px] bg-white border border-[#e8e1e6] rounded-xl text-[#2b262e] text-sm placeholder:text-[#a0969e] outline-none focus:ring-2 focus:ring-[#8A7296] focus:border-transparent transition-all"
                    placeholder="000 000 0000"
                    required
                  />
                </div>
              </div>

              {/* Message Field */}
              <div>
                <label htmlFor="message" className="block font-['Archivo',sans-serif] text-[16px] font-normal text-[#6e6672] leading-[34px] tracking-[2%] mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  className="w-full p-[20px] bg-white border border-[#e8e1e6] rounded-xl text-[#2b262e] text-sm placeholder:text-[#a0969e] outline-none focus:ring-2 focus:ring-[#8A7296] focus:border-transparent transition-all resize-none"
                  placeholder="Type your message here..."
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="px-[30px] py-[22px] bg-[#8A7296] text-white text-sm font-medium rounded-xl hover:bg-[#7a647d] transition-colors inline-flex items-center gap-2"
              >
                Submit Message
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            </form>
          </div>
        </div>
        </div>
      </main>

      <HomepageFooter />
    </div>
  );
}
